/**
 * Pinia account store (frontend-rearchitecture, PR-6).
 *
 * Holds the authenticated account's identity, capabilities, and auth token.
 * Hydrated from the `/api/auth/login` response (see PR-4: login now returns
 * a flat `capabilities: string[]` derived from `Account.capability_keys()`).
 *
 * Persistence model:
 *   - `token` is mirrored to `localStorage` because the rest of the app
 *     (axios interceptors, legacy Vuex store) still reads the token from
 *     localStorage. This store is the source of truth going forward, but
 *     the localStorage write keeps existing consumers working.
 *   - `capabilities` and `accountId` live in memory only. They re-hydrate
 *     from `/api/auth/login` on login, and from `GET /auth/me` via
 *     `rehydrateFromServer()` on page reload (issue #901) -- which also
 *     picks up a role change made since the last login.
 *
 * This PR is purely additive — it does not yet replace any
 * `localStorage.getItem('roleName')` reads. That sweep is PR-7+.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from '@/axios'
import { resetAuthInterceptorState } from '@/interceptors/authResponseInterceptor'
// Session PROVENANCE -- "was this session obtained with credentials?" -- as
// stated by the server on every auth door. Recorded here because this store is
// where all three doors converge: `hydrate` (login), `rehydrateFromServer`
// (/auth/me, the page-reload door) and `clear` (logout). The storefront
// session gate reads it instead of comparing account ids, which was true for
// exactly one account and demoted every real shopper (GH #3242).
import {
    recordSessionProvenance,
    clearSessionProvenance,
} from '@/router/sessionProvenance'

export const useAccountStore = defineStore('account', () => {
    // ---- state ----
    const accountId = ref(null)
    const capabilities = ref([])
    const token = ref(null)
    // PR-9: tenant context (distributor id + feature flags). Null for
    // accounts with no distributor (e.g., Super Admin) or before hydrate.
    // Shape: { id, feature_flags } — matches the `/api/auth/login` payload.
    const distributor = ref(null)
    // True when the held `token` is an ANONYMOUS storefront session (the
    // per-visitor account minted to carry a cart), as the server stated it.
    // Memory-only on purpose: it scopes the in-memory bearer fallback in
    // router/sessionBearer.js to that one population. See there for why a
    // credentialed token must never be served from memory.
    const anonymousStorefront = ref(false)

    // ---- getters ----
    // Empty string is treated as unauthenticated — symmetric with the
    // localStorage mirror gate below (review feedback on PR #886).
    const isAuthenticated = computed(() => Boolean(token.value))

    // ---- actions ----
    /**
     * Populate state from a login response payload.
     *
     * Expected shape: { token, account_id, capabilities }
     * Backwards compat: if `capabilities` is missing/null (older API
     * responses prior to PR-4), defaults to `[]` rather than throwing.
     */
    function hydrate(loginResponse) {
        const payload = loginResponse || {}
        token.value = payload.token ?? null
        accountId.value = payload.account_id ?? null
        capabilities.value = Array.isArray(payload.capabilities)
            ? payload.capabilities
            : []
        // PR-9: distributor is `{id, feature_flags}` or null. Older API
        // responses (pre-PR-9) omit the field — default to null in that case.
        distributor.value = payload.distributor ?? null
        anonymousStorefront.value = payload.session_is_anonymous_storefront === true

        // Record what the server said about HOW this session was obtained.
        // Unconditional, and BEFORE the token mirror: a payload that omits the
        // field clears the record rather than leaving the PREVIOUS session's
        // answer in place to describe this one.
        recordSessionProvenance(payload.session_is_anonymous_storefront)

        // Mirror token to localStorage for legacy consumers (axios
        // interceptor, existing Vuex store). Guard against environments
        // where localStorage is unavailable or non-functional (SSR; some
        // jsdom configurations expose a plain object without setItem).
        if (token.value) {
            try {
                localStorage.setItem('token', token.value)
            } catch {
                // Storage refused the write. The token is held in memory
                // above; for an anonymous storefront session that is what the
                // request interceptor falls back to.
            }
            // Login success: re-arm the auth response interceptor. If a prior
            // session ended (its 401s latched the interceptor into inert
            // "session dead" mode), a fresh login must clear that latch so the
            // retry/redirect machinery works for the new session.
            resetAuthInterceptorState()
        }
    }

    /**
     * Hold an ALREADY-ESTABLISHED anonymous storefront session's token in
     * memory, without touching capabilities or the rest of the session.
     *
     * For the storefront gate's revisit path: the token came from storage on
     * a cold load (no mint happened, so `hydrate` has no payload to read),
     * and the browser can drop that storage key mid-session (WebKit, prod
     * 2026-10-01). Holding it here is what lets the next request still carry
     * a bearer.
     */
    function holdAnonymousStorefrontToken(value) {
        if (!value) return
        token.value = value
        anonymousStorefront.value = true
    }

    /**
     * Re-fetch capabilities from the server for the token we already hold.
     *
     * `hydrate()` populates `capabilities` from the login response and they
     * live in memory only, so a page reload -- or an admin changing this
     * account's role mid-session -- leaves the SPA acting on a stale snapshot
     * from last login. `GET /auth/me` is the canonical rehydration source
     * (its own docstring says so) and returns the login payload shape MINUS
     * `token`.
     *
     * Scope of the problem this fixes: the backend remains the authority, so
     * a stale client cannot PERFORM a revoked action -- the server rejects
     * it. What is wrong is the UI: controls shown that no longer work, and
     * controls hidden that now should appear.
     *
     * Failure policy -- degrade to current behaviour, never worse:
     *   - No token: no request. Nothing to rehydrate against, and an
     *     unauthenticated GET would only 401.
     *   - Request fails (network, 5xx): keep existing state. A refresh that
     *     cannot reach the server must not empty a working store.
     *   - 401: ALSO keep existing state. A dead token is the auth response
     *     interceptor's job -- it already owns the redirect/latch path. A
     *     second, competing logout mechanism here would race it.
     */
    async function rehydrateFromServer() {
        if (!token.value) {
            return
        }

        let payload
        try {
            const response = await axios.get('/auth/me')
            payload = response?.data
        } catch {
            // Deliberately swallowed -- see the failure policy above. The
            // interceptor handles 401s; everything else degrades to the
            // capabilities we already have.
            return
        }

        if (!payload) {
            return
        }

        // Assign field-by-field rather than wholesale: `/auth/me` omits
        // `token`, so spreading the response over state would blank it and
        // log the user out.
        accountId.value = payload.account_id ?? accountId.value
        capabilities.value = Array.isArray(payload.capabilities)
            ? payload.capabilities
            : capabilities.value
        distributor.value = payload.distributor ?? null
        anonymousStorefront.value = payload.session_is_anonymous_storefront === true

        // The RELOAD door, and the one the storefront gate actually depends
        // on: Vuex and Pinia are memory-only, so on a cold load of
        // /shop/<slug> this is the only place provenance can be re-learned.
        // Recorded unconditionally for the same reason as in `hydrate`.
        recordSessionProvenance(payload.session_is_anonymous_storefront)
    }

    /**
     * Reset the store to its initial state. Used on logout.
     * Also removes the mirrored token from localStorage.
     */
    function clear() {
        accountId.value = null
        capabilities.value = []
        token.value = null
        distributor.value = null
        anonymousStorefront.value = false

        try {
            localStorage.removeItem('token')
        } catch {
            // Storage unavailable: nothing persisted to remove.
        }

        // Provenance describes THIS session, so it must not outlive it. Left
        // behind it would mis-describe the next one -- the same failure the
        // anonymous marker had in GH #2941 defect 2.
        clearSessionProvenance()
    }

    return {
        // state
        accountId,
        capabilities,
        token,
        distributor,
        anonymousStorefront,
        // getters
        isAuthenticated,
        // actions
        hydrate,
        holdAnonymousStorefrontToken,
        rehydrateFromServer,
        clear,
    }
})
